/**
 * PreviewCardQT — "How it looks in the library" preview for a Quick
 * Tap. Sibling of the session PreviewCard with quick-tap field
 * names; same `.lib-tile` recipe so the visual reads as a unified
 * library card across content types.
 *
 *   • Background image — `quick_tap_image` (preview or hosted)
 *   • Free pill (top-left) — when NOT premium
 *   • New ribbon (top-right) — when `quick_tap_is_new`
 *   • Crumb — parent name resolved from `associatedParent`
 *   • Title — `quick_tap_title`
 *   • Meta — author name + duration
 *   • Play button — teal circle (decorative)
 *
 * Footer surfaces the universal_link when available — Quick Taps
 * ship with a Branch.io link too. Falls back to a "not yet
 * generated" hint on new sessions.
 */
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import RailCard, { RailCardHead } from '../../redesign/edit/RailCard';
import { WebAppQuickTapVideoIcon } from '../../redesign/edit/webappPreviewIcons';

function parseAssociatedParent(value) {
  if (!value || value === '0') return null;
  const m = String(value).match(/^(\d+)(CAT|SUB)$/);
  if (!m) return null;
  return { id: Number(m[1]), suffix: m[2] };
}

function toArray(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  return Object.values(raw);
}

export default function PreviewCardQT({ formData }) {
  const authors = useSelector((s) => s.authors?.authors) || [];
  const rawCategories = useSelector((s) => s.categories?.all_categories);
  const rawSubcategories = useSelector((s) => s.subcategories?.subcategories);

  const parentName = useMemo(() => {
    const parsed = parseAssociatedParent(formData.associatedParent);
    if (!parsed) return null;
    if (parsed.suffix === 'CAT') {
      const hit = toArray(rawCategories).find(
        (c) => Number(c.category_id) === parsed.id,
      );
      return hit?.category_title || null;
    }
    if (parsed.suffix === 'SUB') {
      const hit = toArray(rawSubcategories).find(
        (sc) => Number(sc.subcategory_id) === parsed.id,
      );
      return hit?.subcategory_title || null;
    }
    return null;
  }, [formData.associatedParent, rawCategories, rawSubcategories]);

  const authorName = useMemo(() => {
    if (!formData.quick_tap_author_id) return null;
    const hit = authors.find(
      (a) => Number(a.author_id) === Number(formData.quick_tap_author_id),
    );
    return hit?.author_name || null;
  }, [formData.quick_tap_author_id, authors]);

  const isFree = !formData.quick_tap_is_premium;
  const isNew = !!formData.quick_tap_is_new;
  const imageUrl = formData.image_preview || formData.quick_tap_image || '';
  const title = formData.quick_tap_title || 'Untitled quick tap';
  const lengthLabel = formData.quick_tap_duration || '';

  const universalLink = formData.universal_link || null;
  const previewHref = universalLink;
  const previewPath = universalLink
    ? universalLink.replace(/^https?:\/\//, '')
    : '— not yet generated —';

  return (
    <RailCard
      variant="preview"
      head={({ open, toggle }) => (
        <RailCardHead
          variant="preview"
          eyebrow="How it looks in the library"
          open={open}
          onToggle={toggle}
        />
      )}
    >
      <div
        style={{
          padding: 16,
          background: 'linear-gradient(180deg, #FAF7F2 0%, #F5EDE4 100%)',
          display: 'grid',
          placeItems: 'center',
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: 240,
            aspectRatio: '216 / 268',
            borderRadius: 14,
            overflow: 'hidden',
            position: 'relative',
            backgroundImage: imageUrl ? `url("${imageUrl}")` : 'none',
            backgroundColor: imageUrl ? 'transparent' : '#2C3E50',
            backgroundPosition: 'center',
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            boxShadow: '0 8px 20px #2C3E5025, 0 1px 0 #FFFFFF80 inset',
          }}
        >
          <span
            aria-hidden
            style={{
              position: 'absolute',
              inset: 0,
              background:
                'linear-gradient(180deg, #00000000 30%, #00000080 65%, #000000C8 100%)',
              pointerEvents: 'none',
            }}
          />

          {isFree && (
            <span
              className="font-condensed font-bold uppercase"
              style={{
                position: 'absolute',
                top: 12,
                left: 12,
                zIndex: 2,
                fontSize: 11,
                letterSpacing: '0.08em',
                color: '#C4715B',
                background: '#FFFCF8',
                padding: '4px 11px',
                borderRadius: 999,
                boxShadow: '0 2px 6px #00000028',
              }}
            >
              Free
            </span>
          )}

          {isNew && (
            <span
              aria-hidden
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: 70,
                height: 70,
                overflow: 'hidden',
                pointerEvents: 'none',
                zIndex: 2,
              }}
            >
              <span
                className="font-condensed font-bold uppercase"
                style={{
                  position: 'absolute',
                  top: 11,
                  right: -26,
                  transform: 'rotate(45deg)',
                  background: 'linear-gradient(135deg, #FFC107, #F5A623)',
                  color: '#2C3E50',
                  fontSize: 10,
                  letterSpacing: '0.12em',
                  padding: '4px 28px',
                  boxShadow: '0 2px 6px #00000028',
                }}
              >
                New
              </span>
            </span>
          )}

          <div
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              padding: '14px 14px 16px',
              zIndex: 1,
              color: '#FFFFFF',
            }}
          >
            {parentName && (
              <div
                className="font-condensed font-bold uppercase"
                style={{
                  fontSize: 9.5,
                  letterSpacing: '0.12em',
                  color: '#FFFFFFCC',
                  lineHeight: 1.3,
                  marginBottom: 6,
                  textShadow: '0 1px 3px #00000060',
                }}
              >
                {parentName}
              </div>
            )}
            <div
              style={{
                fontSize: 14,
                fontWeight: 700,
                lineHeight: 1.2,
                letterSpacing: '-0.005em',
                color: '#FFFFFF',
                textShadow: '0 1px 4px #00000060',
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                marginBottom: 8,
              }}
            >
              {title}
            </div>
            {(authorName || lengthLabel) && (
              <div
                className="flex items-center"
                style={{
                  gap: 8,
                  fontSize: 11.5,
                  color: '#FFFFFFCC',
                  lineHeight: 1.2,
                  textShadow: '0 1px 3px #00000060',
                }}
              >
                {authorName && (
                  <span style={{ fontWeight: 600, color: '#FFFFFF' }}>{authorName}</span>
                )}
                {authorName && lengthLabel && (
                  <span
                    aria-hidden
                    style={{
                      width: 3,
                      height: 3,
                      borderRadius: '50%',
                      background: '#FFFFFF80',
                      flexShrink: 0,
                    }}
                  />
                )}
                {lengthLabel && <span>{lengthLabel} min</span>}
              </div>
            )}
          </div>

          {/* WebApp video-camera icon. Locked variant shown when the
              quick tap is premium (matches what free users see). */}
          <div
            style={{
              position: 'absolute',
              bottom: 12,
              right: 12,
              zIndex: 3,
            }}
          >
            <WebAppQuickTapVideoIcon size={36} isLocked={!!formData.quick_tap_is_premium} />
          </div>
        </div>
      </div>

      <div
        className="flex items-center justify-between"
        style={{
          padding: '10px 14px',
          borderTop: '1px solid #E8E0CF',
          background: '#FAF4EA',
          fontSize: 11,
          gap: 8,
        }}
      >
        <span
          className="font-mono text-text-muted"
          style={{
            fontSize: 10.5,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            flex: 1,
            minWidth: 0,
          }}
        >
          {previewPath}
        </span>
        {previewHref && (
          <a
            href={previewHref}
            target="_blank"
            rel="noopener noreferrer"
            className="font-condensed font-bold uppercase no-underline"
            style={{
              fontSize: 10.5,
              letterSpacing: '0.08em',
              color: '#5C7E72',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              whiteSpace: 'nowrap',
            }}
          >
            Open ↗
          </a>
        )}
      </div>
    </RailCard>
  );
}
